import { Button, ButtonDropdown, ButtonsGroup, NavItem, NavItemAccessor, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { customerModalTypeMap } from '@features/contacts/constants/customerModalTypeMap'
import { CustomerModal } from '@features/contacts/elements/CustomerModal'
import { CustomerModalSubmitActionType } from '@features/contacts/enums/customerModalSubmitActionType'
import { Contact } from '@features/contacts/types/contact'
import { ContactType } from '@features/contacts/types/contactType'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'

import { queryClient } from '../../../../../../config/queryClient'
import { ModalId } from '../../../../../../enums/modalId'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { useOrganizationSource, useSegment } from '../../../../../../hooks'
import { useOrganizationViewUrl } from '../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { useContactsEmberConnection } from '../../../../context/contactsEmberConnectionContext'
import { CREATE_CONTACT_PERSON_MODAL_ID } from '../ContactPersonsTable/elements/ContactPersonModal'
import { CreateActionId } from './enums/createActionId'

interface ContactDetailSideActionsProps {
  contact?: Contact
  isEditing?: boolean
}

export const ContactDetailSideActions = ({
  contact,
  isEditing = false,
}: ContactDetailSideActionsProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const navigate = useNavigate()
  const { url: viewClientUrl } = useOrganizationViewUrl(reactRoute.organization.clients.getViewClientRoute(contact?.id))

  const handleCustomerModalClose = useCallback(() => {
    if (isEditing && viewClientUrl) {
      navigate(viewClientUrl, { replace: true })
    }
  }, [isEditing, navigate, viewClientUrl])

  const { open: openCustomerModal } = useModal(ModalId.CustomerModal, { onClose: handleCustomerModalClose })
  const { open: openCreateContactPersonModal } = useModal(CREATE_CONTACT_PERSON_MODAL_ID)
  const { isSourceBilly } = useOrganizationSource()
  const shouldUseLegacyCustomerModal = isSourceBilly
  const { editContact } = useContactsEmberConnection()
  const { url: invoiceNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(getInvoiceDocumentRouteBase(InvoiceDocumentType.Invoice)),
  )
  const { url: quoteNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(getInvoiceDocumentRouteBase(InvoiceDocumentType.Quote)),
  )

  const dropdownItems: NavItem<CreateActionId>[] = useMemo(
    () => [
      {
        children: t('contact.side_actions.create_invoice'),
        iconLeft: 'fileText',
        id: CreateActionId.Invoice,
        value: CreateActionId.Invoice,
      },
      {
        children: t('contact.side_actions.create_quote'),
        iconLeft: 'fileCheck',
        id: CreateActionId.Quote,
        value: CreateActionId.Quote,
      },
      {
        accessor: NavItemAccessor.Separator,
        children: '',
        id: 'separator-1',
        value: '' as CreateActionId,
      },
      {
        accessorGroupId: 'separator-1',
        children: t('contact.side_actions.create_contact'),
        iconLeft: 'userRound',
        id: CreateActionId.Contact,
        value: CreateActionId.Contact,
      },
    ],
    [t],
  )

  const handleSelect = useCallback(
    (_id: string, value: string) => {
      if (!value) {
        return
      }

      track('XXX - Invoicing Commerce - Client Details - Create menu option clicked', { option: value })

      if (value === CreateActionId.Invoice && invoiceNewUrl) {
        navigate(`${invoiceNewUrl}?contact_id=${contact?.id}`)
        return
      }

      if (value === CreateActionId.Quote && quoteNewUrl) {
        navigate(`${quoteNewUrl}?contact_id=${contact?.id}`)
        return
      }

      if (value === CreateActionId.Contact) {
        openCreateContactPersonModal()
      }
    },
    [contact?.id, invoiceNewUrl, navigate, openCreateContactPersonModal, quoteNewUrl, track],
  )

  const handleCustomerModalSubmit = useCallback(() => {
    if (contact?.id) {
      queryClient.invalidateQueries([QueryKeys.Contact, contact.id])
    }
  }, [contact?.id])

  const handleEditClick = useCallback(() => {
    if (!contact) {
      return
    }
    track('XXX - Invoicing Commerce - Client Details - Edit button clicked')
    if (shouldUseLegacyCustomerModal) {
      editContact(contact.id)
      return
    }
    openCustomerModal()
  }, [contact, editContact, shouldUseLegacyCustomerModal, openCustomerModal, track])

  return (
    <>
      <ButtonsGroup wrap={false}>
        <Button disabled={!contact} icon="squarePen" onClick={handleEditClick} variant="outlined">
          {t('edit')}
        </Button>
        <ButtonDropdown disabled={!contact} items={dropdownItems} onSelect={handleSelect}>
          {t('contact.side_actions.create')}
        </ButtonDropdown>
      </ButtonsGroup>
      {!shouldUseLegacyCustomerModal && (
        <CustomerModal
          customer={contact}
          defaultCustomerType={contact ? customerModalTypeMap[contact.type] : customerModalTypeMap[ContactType.Person]}
          // The form reads `customer` only once, so it must not render before the contact has loaded.
          forceRender={isEditing && !!contact}
          id={ModalId.CustomerModal}
          onSubmit={handleCustomerModalSubmit}
          submitActionType={CustomerModalSubmitActionType.Edit}
        />
      )}
    </>
  )
}
